import { Injectable } from '@angular/core';
import mixpanel from 'mixpanel-browser';
import { environment } from '../../../../environments/environment';
import { AnalyticsEvents } from './AnalyticsEvents';

@Injectable({
  providedIn: 'root',
})
export class AnalyticsService {
  private consentGiven = false;
  private isIdentified = false;

  init(): void {
    if (this.allowAnalytics()) {
      mixpanel.init(environment.analytics.mixpanelProjectToken, {
        debug: !environment.production,
        // eslint-disable-next-line @typescript-eslint/naming-convention
        api_host: 'https://api-eu.mixpanel.com',
      });
      this.registerSuperProperties();
    }
  }

  private registerSuperProperties(): void {
    /* eslint-disable @typescript-eslint/naming-convention */
    mixpanel.register({
      Source: 'SecurityPortal',
      Country: this.detectCountry(),
      OS: this.detectOperatingSystem(),
    });
  }

  private detectCountry(): string {
    try {
      const { region } = new Intl.Locale(navigator.language);
      return region ?? 'unknown';
    } catch {
      return 'unknown';
    }
  }

  private detectOperatingSystem(): string {
    const { platform } =
      (navigator as Navigator & { userAgentData?: { platform?: string } })
        .userAgentData ?? {};
    if (platform) {
      return platform;
    }

    const { userAgent } = navigator;
    if (/Windows/i.test(userAgent)) {
      return 'Windows';
    }
    if (/Macintosh|Mac OS X/i.test(userAgent)) {
      return 'macOS';
    }
    if (/Android/i.test(userAgent)) {
      return 'Android';
    }
    if (/iPhone|iPad|iPod/i.test(userAgent)) {
      return 'iOS';
    }
    if (/Linux/i.test(userAgent)) {
      return 'Linux';
    }
    return 'unknown';
  }

  identify(userToken: string): void {
    if (this.allowAnalytics()) {
      mixpanel.identify(userToken);
      this.isIdentified = true;
    }
  }

  reset(): void {
    if (this.allowAnalytics()) {
      mixpanel.reset();
    }
  }

  track<E extends AnalyticsEvents['eventName']>(
    eventName: E,
    props?: Extract<AnalyticsEvents, { eventName: E }>['props'],
  ) {
    if (this.allowAnalytics()) {
      mixpanel.track(eventName, props);
    }
  }

  setConsent(consentGiven: boolean): void {
    this.consentGiven = consentGiven;
    this.updateAnalyticsState();
  }

  get isUserIdentified() {
    return this.isIdentified;
  }

  private updateAnalyticsState(): void {
    if (!this.consentGiven) {
      this.reset();
      return;
    }
    this.init();
  }

  private allowAnalytics(): boolean {
    return environment.analytics.enableAnalytics && this.consentGiven;
  }
}
